import { createContext, useContext, useMemo, ReactNode } from "react";
import { useLocation } from "react-router-dom";

export type Language = "it" | "en";

interface LanguageContextType {
  language: Language;
  isEnglish: boolean;
  getLocalizedPath: (italianPath: string) => string;
  getAlternatePath: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Route mapping: Italian path -> English path
const routeMap: Record<string, string> = {
  "/": "/en",
  "/chi-siamo": "/en/about-us",
  "/servizi": "/en/services",
  "/portfolio": "/en/portfolio",
  "/contatti": "/en/contact",
  "/preventivo": "/en/quote",
  "/privacy-policy": "/en/privacy-policy",
  "/cookie-policy": "/en/cookie-policy",
};

// Reverse mapping: English path -> Italian path
const reverseRouteMap: Record<string, string> = Object.entries(routeMap).reduce(
  (acc, [it, en]) => ({ ...acc, [en]: it }),
  {}
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  const value = useMemo(() => {
    const isEnglish = location.pathname.startsWith("/en");
    const language: Language = isEnglish ? "en" : "it";

    const getLocalizedPath = (italianPath: string): string => {
      if (language === "en") {
        return routeMap[italianPath] || italianPath;
      }
      return italianPath;
    };

    const getAlternatePath = (): string => {
      const currentPath = location.pathname;
      
      if (isEnglish) {
        // Currently English, return Italian path
        return reverseRouteMap[currentPath] || "/";
      } else {
        // Currently Italian, return English path
        return routeMap[currentPath] || "/en";
      }
    };

    return {
      language,
      isEnglish,
      getLocalizedPath,
      getAlternatePath,
    };
  }, [location.pathname]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
